<script setup>
// Deck Detail — view one library deck: metadata, a generation "pipeline" panel
// (trigger the bake + poll status), and the items table. Edit / delete / practise
// from here. Generation is async server-side (lib/generationQueue.js): we POST
// /generate then poll GET /:id until the deck leaves queued/generating.
import { ref, onMounted, onBeforeUnmount, computed, defineAsyncComponent } from "vue";
import { useRoute, useRouter } from "vue-router";
// Lazy — keeps Tabulator (heavy) out of the main bundle; it loads only when a
// deck detail page is opened.
const LibraryItemsGrid = defineAsyncComponent(() => import("./LibraryItemsGrid.vue"));

const route = useRoute();
const router = useRouter();
const id = route.params.id;

const loading = ref(true);
const error = ref("");
const deck = ref(null);
const busy = ref(false);       // generate/delete in flight
const actionError = ref("");
const reviewBusy = ref(false); // AI check / approve in flight
const reviewError = ref("");
const sourcesText = ref("");   // textarea bound to review.sources (one per line)
let pollTimer = null;

const LETTERS = ["A", "B", "C", "D"];
const STATUS = {
  draft:      { cls: "bg-secondary",         label: "Draft" },
  queued:     { cls: "bg-info text-dark",    label: "Queued" },
  generating: { cls: "bg-warning text-dark", label: "Generating…" },
  ready:      { cls: "bg-success",           label: "Ready" },
  error:      { cls: "bg-danger",            label: "Error" }
};
const badge = computed(() => STATUS[deck.value?.status] || { cls: "bg-secondary", label: deck.value?.status || "—" });
const isBaking = computed(() => deck.value && ["queued", "generating"].includes(deck.value.status));

// --- content review (DW-006) ----------------------------------------------
const REVIEW_STATUS = {
  unreviewed: { cls: "bg-secondary",      label: "Unreviewed" },
  ai_checked: { cls: "bg-info text-dark", label: "AI-checked" },
  approved:   { cls: "bg-success",        label: "Approved" }
};
const review = computed(() => deck.value?.review || { status: "unreviewed", sources: [], aiVerdicts: [] });
const reviewBadge = computed(() => REVIEW_STATUS[review.value.status] || REVIEW_STATUS.unreviewed);
const gate = computed(() => deck.value?.generateGate || { allowed: false, reason: "" });
const suspectVerdicts = computed(() => (review.value.aiVerdicts || []).filter((v) => v.verdict === "suspect"));
const progressPct = computed(() => {
  const g = deck.value?.generation;
  if (!g?.total) return 0;
  return Math.min(100, Math.round((g.done / g.total) * 100));
});

// --- distractor-scaffold summary (bulk import) ----------------------------
const scaffold = computed(() => deck.value?.scaffold || null);
const scaffoldFailures = computed(() => Array.isArray(scaffold.value?.failures) ? scaffold.value.failures : []);
// Workspace turned content review off → the bake gate is bypassed.
const reviewBypassed = computed(() => deck.value?.generateGate?.bypassed === true);

// --- cover ----------------------------------------------------------------
const cover = computed(() => deck.value?.cover || null);
const coverBusy = ref(false);
const coverError = ref("");
// In flight = queued (waiting for the cover worker) OR generating (claimed, rendering).
// Both must keep the poll alive and the button disabled — a cover sits in `queued`
// for up to one 20s poll before a worker claims it.
const coverGenerating = computed(() => ["queued", "generating"].includes(cover.value?.status));
async function regenerateCover() {
  coverError.value = ""; coverBusy.value = true;
  try {
    const r = await fetch(`/api/library/decks/${id}/cover`, { method: "POST", credentials: "include" });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.error || "Couldn't start cover generation.");
    await load(); // status → queued; polling continues until ready/error
  } catch (e) { coverError.value = e.message; } finally { coverBusy.value = false; }
}

// --- archive --------------------------------------------------------------
const archiveBusy = ref(false);
const isArchived = computed(() => Boolean(deck.value?.archivedAt));
async function toggleArchive() {
  archiveBusy.value = true; actionError.value = "";
  try {
    const r = await fetch(`/api/library/decks/${id}/archive`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ archived: !isArchived.value })
    });
    if (!r.ok) throw new Error("Couldn't update the archive state.");
    await load();
  } catch (e) { actionError.value = e.message; } finally { archiveBusy.value = false; }
}

// --- next content-review date (part of the review block) ------------------
const nextReviewDate = ref("");

async function load() {
  try {
    const r = await fetch(`/api/library/decks/${id}`, { credentials: "include" });
    if (!r.ok) {
      if (r.status !== 404) console.error("[library] deck load:", r.status);
      error.value = r.status === 404 ? "That deck isn't in your library." : "We couldn't load this deck just now.";
      return;
    }
    deck.value = (await r.json()).deck;
    sourcesText.value = (deck.value.review?.sources || []).join("\n");
    nextReviewDate.value = deck.value.review?.nextReviewDate || "";
    // Keep polling while a bake OR a cover generation is in progress.
    if (isBaking.value || coverGenerating.value) schedulePoll(); else clearPoll();
  } catch (e) {
    console.error("[library] deck load failed:", e);
    error.value = "We couldn't load this deck just now.";
  } finally {
    loading.value = false;
  }
}

function schedulePoll() { clearPoll(); pollTimer = setTimeout(load, 3000); }
function clearPoll() { if (pollTimer) { clearTimeout(pollTimer); pollTimer = null; } }

async function generate() {
  actionError.value = "";
  busy.value = true;
  try {
    const r = await fetch(`/api/library/decks/${id}/generate`, { method: "POST", credentials: "include" });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.error || "Generation couldn't start just now — please try again.");
    await load(); // picks up status=queued + starts polling
  } catch (e) {
    console.error("[library] generate failed:", e);
    actionError.value = e.message;
  } finally {
    busy.value = false;
  }
}

// Run the AI adversarial accuracy check (→ ai_checked). Independent of the
// generation prompt by design (DW-006).
async function runReview() {
  reviewError.value = "";
  reviewBusy.value = true;
  try {
    const r = await fetch(`/api/library/decks/${id}/review`, { method: "POST", credentials: "include" });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.error || "The accuracy check couldn't run just now — please try again.");
    await load();
  } catch (e) {
    console.error("[library] review failed:", e);
    reviewError.value = e.message;
  } finally {
    reviewBusy.value = false;
  }
}

// Human sign-off (→ approved). The only path to "approved" — the AI check cannot
// grant it (DW-006 item 3).
async function approve() {
  reviewError.value = "";
  reviewBusy.value = true;
  try {
    const sources = sourcesText.value.split("\n").map((s) => s.trim()).filter(Boolean);
    const r = await fetch(`/api/library/decks/${id}/review/approve`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sources, nextReviewDate: nextReviewDate.value || "" })
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.error || "We couldn't record approval just now — please try again.");
    await load();
  } catch (e) {
    console.error("[library] approve failed:", e);
    reviewError.value = e.message;
  } finally {
    reviewBusy.value = false;
  }
}

async function remove() {
  if (!confirm(`Delete "${deck.value?.topic}" and all its questions? This cannot be undone.`)) return;
  busy.value = true;
  try {
    const r = await fetch(`/api/library/decks/${id}`, { method: "DELETE", credentials: "include" });
    if (!r.ok && r.status !== 204) throw new Error("We couldn't delete the deck just now — please try again.");
    router.push("/library");
  } catch (e) {
    console.error("[library] delete failed:", e);
    actionError.value = e.message;
    busy.value = false;
  }
}

// --- sharing / publish (Phase 3) ------------------------------------------
const visBusy = ref(false);
const visError = ref("");
const isPublic = computed(() => deck.value?.visibility === "public");

async function setVisibility(visibility) {
  visError.value = "";
  visBusy.value = true;
  try {
    const r = await fetch(`/api/library/decks/${id}/visibility`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ visibility })
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.error || "We couldn't update visibility just now — please try again.");
    await load();
  } catch (e) {
    console.error("[library] visibility failed:", e);
    visError.value = e.message;
  } finally {
    visBusy.value = false;
  }
}

onMounted(load);
onBeforeUnmount(clearPoll);
</script>

<template>
  <div class="container-fluid p-0">
    <router-link to="/library" class="btn btn-sm btn-link px-0 mb-2"><i class="bi bi-arrow-left me-1"></i>My Library</router-link>

    <div v-if="loading" class="text-muted py-4">
      <span class="spinner-border spinner-border-sm me-2"></span>Loading…
    </div>
    <div v-else-if="error" class="card border-0 shadow-sm">
      <div class="card-body text-center py-5 text-muted">
        <i class="bi bi-collection display-6 d-block mb-2"></i>
        <p class="mb-3">{{ error }}</p>
        <router-link to="/library" class="btn btn-outline-secondary btn-sm">Back to My Library</router-link>
      </div>
    </div>

    <template v-else-if="deck">
      <!-- Header -->
      <div class="d-flex align-items-start justify-content-between mb-3">
        <div>
          <h1 class="h3 mb-1">
            {{ deck.topic }}
            <span class="badge align-middle ms-1" :class="badge.cls">{{ badge.label }}</span>
            <span v-if="isArchived" class="badge align-middle ms-1 bg-secondary"><i class="bi bi-archive me-1"></i>Archived</span>
          </h1>
          <div class="text-muted small">
            <span v-if="deck.area"><i class="bi bi-tag me-1"></i>{{ deck.area }} · </span>
            <code>{{ deck.slug }}</code>
            <span v-if="deck.version"> · v{{ deck.version }}</span>
            · {{ deck.items.length }} question{{ deck.items.length === 1 ? "" : "s" }}
          </div>
          <p v-if="deck.description" class="text-muted mb-0 mt-1">{{ deck.description }}</p>
          <p v-if="deck.byline" class="text-muted small mb-0"><i class="bi bi-person-badge me-1"></i>{{ deck.byline }}</p>
          <div v-if="deck.tags?.length" class="mt-2">
            <span v-for="t in deck.tags" :key="t" class="badge bg-light text-dark border me-1">{{ t }}</span>
          </div>
        </div>
        <div class="d-flex gap-2 flex-shrink-0">
          <router-link :to="`/library/${deck.id}/edit`" class="btn btn-sm btn-outline-secondary"><i class="bi bi-pencil me-1"></i>Edit</router-link>
          <button class="btn btn-sm btn-outline-secondary" :disabled="archiveBusy" @click="toggleArchive">
            <span v-if="archiveBusy" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi me-1" :class="isArchived ? 'bi-arrow-counterclockwise' : 'bi-archive'"></i>{{ isArchived ? "Restore" : "Archive" }}
          </button>
          <button class="btn btn-sm btn-outline-danger" :disabled="busy" @click="remove"><i class="bi bi-trash me-1"></i>Delete</button>
        </div>
      </div>

      <div v-if="actionError" class="alert alert-warning py-2">{{ actionError }}</div>

      <!-- Cover — the generated background art (title/stats are an overlay in the
           library). Regenerate to refresh it; text-only decks still get a cover. -->
      <div class="card border-0 shadow-sm mb-3">
        <div class="card-body">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <h2 class="h6 mb-0"><i class="bi bi-image me-2"></i>Cover</h2>
            <button class="btn btn-sm btn-outline-secondary" :disabled="coverBusy || coverGenerating" @click="regenerateCover">
              <span v-if="coverBusy || coverGenerating" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-magic me-1"></i>{{ cover?.imageUrl ? "Regenerate" : "Generate cover" }}
            </button>
          </div>
          <div v-if="coverError" class="alert alert-warning py-2 mb-2">{{ coverError }}</div>
          <div v-if="coverGenerating" class="text-muted small"><span class="spinner-border spinner-border-sm me-2"></span>Generating cover… this takes ~30–60s.</div>
          <template v-else-if="cover?.imageUrl">
            <img :src="cover.imageUrl" alt="Deck cover" class="rm-cover-img">
            <p v-if="cover.tagline" class="text-muted small mt-2 mb-0"><i class="bi bi-quote me-1"></i>{{ cover.tagline }}</p>
          </template>
          <p v-else-if="cover?.status === 'error'" class="text-danger small mb-0"><i class="bi bi-exclamation-triangle me-1"></i>{{ cover.error || "Cover generation failed." }} Try again.</p>
          <p v-else class="text-muted small mb-0">No cover yet — generate a background image for this deck.</p>
        </div>
      </div>

      <!-- Distractor-scaffold summary — what the bulk-import auto-distractor pass
           did: items it couldn't complete, and items that used the relaxed
           short-answer fallback. -->
      <div v-if="scaffold && (scaffoldFailures.length || scaffold.lenientCount)" class="alert py-2" :class="scaffoldFailures.length ? 'alert-warning' : 'alert-info'">
        <div v-if="scaffoldFailures.length" :class="{ 'mb-1': scaffold.lenientCount }">
          <i class="bi bi-exclamation-triangle me-1"></i>
          <strong>{{ scaffoldFailures.length }} question{{ scaffoldFailures.length === 1 ? "" : "s" }}</strong>
          couldn't get auto-generated distractors:
          <span class="fw-semibold">{{ scaffoldFailures.map(f => "Q" + f.questionId).join(", ") }}</span>.
          Open <router-link :to="`/library/${deck.id}/edit`">Edit</router-link> to add distractors or reword them.
        </div>
        <div v-if="scaffold.lenientCount" class="small mb-0" :class="scaffoldFailures.length ? 'text-muted' : ''">
          <i class="bi bi-info-circle me-1"></i>{{ scaffold.lenientCount }} item{{ scaffold.lenientCount === 1 ? "" : "s" }} used a relaxed distractor quality check (short-answer fallback).
        </div>
      </div>

      <!-- Content review panel (DW-006) — accuracy check + human sign-off. Gates
           audio generation: the bake button below stays disabled until this
           passes. -->
      <div class="card border-0 shadow-sm mb-3">
        <div class="card-body">
          <div class="d-flex align-items-center justify-content-between">
            <h2 class="h6 mb-0">
              <i class="bi bi-clipboard-check me-2"></i>Content review
              <span class="badge align-middle ms-1" :class="reviewBadge.cls">{{ reviewBadge.label }}</span>
            </h2>
            <button class="btn btn-sm btn-outline-secondary" :disabled="reviewBusy" @click="runReview">
              <span v-if="reviewBusy" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-robot me-1"></i>Run AI accuracy check
            </button>
          </div>

          <div v-if="reviewError" class="alert alert-warning py-2 mt-3 mb-0">{{ reviewError }}</div>

          <div v-if="reviewBypassed" class="alert alert-secondary py-2 mt-3 mb-0 small">
            <i class="bi bi-shield-slash me-1"></i>Content review is <strong>turned off for this workspace</strong> — audio can be generated without sign-off. You can still run the AI check below; change the policy in <router-link to="/settings/library">Settings → Library</router-link>.
          </div>

          <!-- AI verdicts -->
          <div v-if="review.aiVerdicts?.length" class="mt-3">
            <div v-if="suspectVerdicts.length === 0" class="text-success small">
              <i class="bi bi-check-circle me-1"></i>AI accuracy check passed — no questions flagged.
            </div>
            <div v-else>
              <p class="text-danger small mb-1"><i class="bi bi-exclamation-triangle me-1"></i>{{ suspectVerdicts.length }} question(s) flagged as suspect:</p>
              <ul class="small mb-0">
                <li v-for="v in suspectVerdicts" :key="v.questionId">
                  <strong>Q{{ v.questionId }}:</strong> {{ v.issue || "flagged" }}
                  <span v-if="v.confidence != null" class="text-muted">(confidence {{ Math.round(v.confidence * 100) }}%)</span>
                </li>
              </ul>
            </div>
            <p v-if="review.aiCheckedAt" class="text-muted small mt-2 mb-0">Last AI check: {{ new Date(review.aiCheckedAt).toLocaleString() }}</p>
          </div>
          <p v-else class="text-muted small mt-3 mb-0">No AI accuracy check has run yet. Run it to catch wrong answers and misleading distractors before paying to generate audio.</p>

          <!-- Human sign-off -->
          <hr class="my-3">
          <label class="form-label small text-muted mb-1">Sources checked against (one per line)</label>
          <textarea v-model="sourcesText" class="form-control form-control-sm mb-2" rows="2" placeholder="e.g. NICE guideline NG196; BRAKE teen-driver module 2"></textarea>
          <div class="mb-2" style="max-width: 260px">
            <label class="form-label small text-muted mb-1">Next review date <span class="text-muted">— optional</span></label>
            <input v-model="nextReviewDate" type="date" class="form-control form-control-sm">
          </div>
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <button class="btn btn-sm btn-success" :disabled="reviewBusy" @click="approve">
              <span v-if="reviewBusy" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-patch-check me-1"></i>{{ review.status === "approved" ? "Re-approve content" : "Approve content" }}
            </button>
            <span v-if="review.status === 'approved'" class="text-success small">
              <i class="bi bi-check-circle me-1"></i>Approved by {{ review.reviewedBy }}<span v-if="review.reviewedAt"> on {{ new Date(review.reviewedAt).toLocaleDateString() }}</span>
            </span>
            <span v-else-if="gate.requiresApproval" class="text-muted small">Human sign-off is required for this deck before audio can be generated.</span>
          </div>
        </div>
      </div>

      <!-- Generation pipeline panel -->
      <div class="card border-0 shadow-sm mb-3">
        <div class="card-body">
          <div class="d-flex align-items-center justify-content-between">
            <h2 class="h6 mb-0"><i class="bi bi-soundwave me-2"></i>Audio generation</h2>
            <button
              v-if="!isBaking"
              class="btn btn-sm btn-primary"
              :disabled="busy || !gate.allowed"
              :title="gate.allowed ? '' : gate.reason"
              @click="generate"
            >
              <i class="bi bi-magic me-1"></i>{{ deck.status === "ready" ? "Re-generate" : "Generate audio" }}
            </button>
          </div>
          <p v-if="!isBaking && !gate.allowed" class="text-muted small mt-2 mb-0">
            <i class="bi bi-shield-lock me-1"></i>{{ gate.reason }}
          </p>

          <div v-if="isBaking" class="mt-3">
            <div class="d-flex justify-content-between small text-muted mb-1">
              <span><span class="spinner-border spinner-border-sm me-2"></span>{{ deck.status === "queued" ? "Queued…" : "Generating clips…" }}</span>
              <span v-if="deck.generation?.total">{{ deck.generation.done || 0 }} / {{ deck.generation.total }}</span>
            </div>
            <div class="progress" style="height: 6px">
              <div class="progress-bar progress-bar-striped progress-bar-animated" :style="{ width: progressPct + '%' }"></div>
            </div>
          </div>
          <p v-else-if="deck.status === 'ready'" class="text-success small mt-2 mb-0"><i class="bi bi-check-circle me-1"></i>Audio ready — it now appears under Practise.</p>
          <p v-else-if="deck.status === 'error'" class="text-danger small mt-2 mb-0"><i class="bi bi-exclamation-triangle me-1"></i>{{ deck.generation?.error || "Generation failed." }} Try again.</p>
          <p v-else class="text-muted small mt-2 mb-0">Not generated yet. Generate the spoken question + feedback audio so the deck can be practised.</p>
        </div>
      </div>

      <!-- Sharing / publish (Phase 3) — make a ready deck public so it appears
           in the everyone-can-see catalog (it stays owned by you). -->
      <div class="card border-0 shadow-sm mb-3">
        <div class="card-body">
          <div class="d-flex align-items-center justify-content-between">
            <h2 class="h6 mb-0">
              <i class="bi bi-globe2 me-2"></i>Sharing
              <span class="badge align-middle ms-1" :class="isPublic ? 'bg-primary' : 'bg-secondary'">{{ isPublic ? "Public" : "Private" }}</span>
            </h2>
            <button
              class="btn btn-sm"
              :class="isPublic ? 'btn-outline-secondary' : 'btn-outline-primary'"
              :disabled="visBusy || (!isPublic && deck.status !== 'ready')"
              :title="(!isPublic && deck.status !== 'ready') ? 'Generate the audio first' : ''"
              @click="setVisibility(isPublic ? 'private' : 'public')"
            >
              <span v-if="visBusy" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi me-1" :class="isPublic ? 'bi-lock' : 'bi-globe'"></i>{{ isPublic ? "Make private" : "Make public" }}
            </button>
          </div>
          <div v-if="visError" class="alert alert-warning py-2 mt-3 mb-0">{{ visError }}</div>
          <p class="text-muted small mt-2 mb-0">
            <template v-if="isPublic">Anyone can find and practise this deck in the public catalog. It stays yours — “Make private” removes it.</template>
            <template v-else-if="deck.status === 'ready'">Private — only you can practise it. Make it public to share it in the everyone-can-see catalog.</template>
            <template v-else><i class="bi bi-shield-lock me-1"></i>Generate the audio first — only a ready deck can be made public.</template>
          </p>
        </div>
      </div>

      <!-- Items — dense, sortable, inline-editable grid (Tabulator). Keyed on
           updatedAt so a successful save remounts it with fresh data. -->
      <div class="card border-0 shadow-sm">
        <div class="card-body">
          <h2 class="h6 mb-3">Questions <span class="text-muted fw-normal">({{ deck.items.length }})</span></h2>
          <LibraryItemsGrid :key="deck.updatedAt" :deck="deck" @saved="load" />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.rm-cover-img {
  width: 100%;
  max-width: 640px;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  object-position: right center;
  border-radius: 12px;
  display: block;
}
</style>
